import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Eye, Plus, Search, X, Trash2, Paperclip, Download, ExternalLink } from 'lucide-react';
import { zipSync } from 'fflate';
import { createConcretagemRealizada, deleteAnexoConcretagem, deleteConcretagemRealizada, getAnexoDownloadUrl, getConcretagemRealizada, updateConcretagemRealizada, uploadAnexosConcretagem } from '../services/concretagemRealizadaService';
import RecordViewModal from '../components/RecordViewModal';
import { createMoldador, getMoldador } from '../services/moldadorService';
import { getObras } from '../services/obraService';
import { getUser } from '../services/usersService';
import { showSuccessToast, showErrorToast } from '../utils/notifications';
import { onlyDigits, formatCpf, formatPhone } from '../utils/formatters';
import useSubmitLock from '../hooks/useSubmitLock';

/*const mockObras = [{ id: 'obra-1', nome: 'Residencial Atlantico' }, { id: 'obra-2', nome: 'Hospital Central' }, { id: 'obra-3', nome: 'Ponte Nova' }];
const mockMoldadores = [{ id: 'mol-1', nome: 'Carlos Silva' }, { id: 'mol-2', nome: 'Ana Souza' }, { id: 'mol-3', nome: 'Marcos Lima' }];
const mockDigitadores = [{ id: 'usr-1', nome: 'Bianca Oliveira' }, { id: 'usr-2', nome: 'Lucas Ferreira' }, { id: 'usr-3', nome: 'Rafaela Santos' }];*/

/*const mockConcretagens = [
  { id: 'conc-1', idObra: 'obra-1', dataConcretagem: '2026-03-20T08:30', qtdCaminhoes: 8, qtdTotalCp: 24, idResponsavelMoldagem: 'mol-1', idResponsavelDigitacao: 'usr-2', anexos: [{ nome: 'ficha-concretagem.pdf', tipo: 'application/pdf', tamanho: 1024 * 300 }] },
];*/

const INITIAL_FORM = { idObra: '', dataConcretagem: '', qtdCaminhoes: '', qtdTotalCp: '', idResponsavelMoldagem: '', idResponsavelDigitacao: '' };
const INITIAL_MOLDADOR_FORM = { nome: '', cpf: '', telefone: '', email: '' };

// Formata um tamanho em bytes para B/KB/MB legível.
function formatFileSize(bytes) {
  if (!bytes && bytes !== 0) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Nome livre dentro do zip: repetidos ganham "(2)", "(3)"... para um não sobrescrever o outro.
function nomeLivreNoZip(arquivos, nome) {
  if (!(nome in arquivos)) return nome;
  const ponto = nome.lastIndexOf('.');
  const base = ponto > 0 ? nome.slice(0, ponto) : nome;
  const extensao = ponto > 0 ? nome.slice(ponto) : '';
  let n = 2;
  while (`${base} (${n})${extensao}` in arquivos) n += 1;
  return `${base} (${n})${extensao}`;
}

// Dispara o download de um blob gerado no navegador com o nome informado.
function salvarArquivo(blob, nome) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nome;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Normaliza para o formato de input datetime-local (YYYY-MM-DDTHH:MM).
function normalizeDateTimeLocal(value) {
  if (!value) return '';
  const asString = String(value);
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(asString)) return asString;
  if (/^\d{4}-\d{2}-\d{2}$/.test(asString)) return `${asString}T00:00`;

  const date = new Date(asString);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (num) => String(num).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// Exibe data e hora no padrão brasileiro (dd/mm/aaaa HH:MM).
function formatDateTimeDisplay(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const pad = (num) => String(num).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// Normaliza uma concretagem realizada da API (obra/empresa/responsáveis/anexos aninhados) para o formato da tela.
function normalizeConcretagem(item) {
  // AJUSTE BACKEND AQUI: chaves aceitas para o retorno de concretagem realizada.
  return {
    id: item.id || item.idConcretagemRealizada,
    idObra: String(item.obra?.idObra || item.idObra || ''),
    nomeObra: String(item.obra?.nomeObra || item.nomeObra || ''),
    idEmpresa: String(item.obra?.empresa?.idEmpresa || item.idEmpresaNavigation?.idEmpresa || item.empresa?.idEmpresa || ''),
    razaoSocialEmpresa: String(item.obra?.empresa?.razaoSocial || item.idEmpresaNavigation?.razaoSocial || item.empresa?.razaoSocial || ''),
    dataConcretagem: normalizeDateTimeLocal(item.data_concretagem || item.dataConcretagem || ''),
    qtdCaminhoes: Number(item.quantidadeCaminhao || item.quantidade_caminhoes || item.qtdCaminhoes || 0),
    qtdTotalCp: Number(item.quantidadeTotalCp || item.quantidade_total_cp || item.qtdTotalCp || 0),
    idResponsavelMoldagem: String(item.idRespMoldagem || item.respMoldagem?.idMoldador || item.id_responsavel_moldagem || item.idResponsavelMoldagem || ''),
    nomeResponsavelMoldagem: String(item.responsavelMoldagem?.nomeCompleto || item.responsavelMoldagem?.nome || item.respMoldagem?.nomeMoldador || ''),
    idResponsavelDigitacao: String(item.idRespDigitacao || item.respDigitacao?.idUsuario || item.id_responsavel_digitacao || item.idResponsavelDigitacao || ''),
    nomeResponsavelDigitacao: String(item.respDigitacao?.nomeCompleto || item.respDigitacao?.nome || ''),
    // AJUSTE BACKEND AQUI: o ideal é o GET de concretagens já trazer a lista da tabela arquivo_concretagem
    // com a URL assinada/publica do arquivo no Azure Storage + nome original + id da concretagem realizada.
    // Exemplo esperado: [{ nomeOriginal, urlArquivo, contentType, tamanho, idConcretagemRealizada }]
    // Assim o front não depende de um campo agregado como 'qtdAnexos' para montar o modal.
    qtdAnexos: Number(item.arquivosConcretagens?.length || item.arquivoConcretagem?.length || 0),
    anexos: Array.isArray(item.anexos)
      ? item.anexos
      : (Array.isArray(item.arquivosConcretagens) ? item.arquivosConcretagens : []),
  };
}

// Normaliza uma obra da API para { idObra, nomeObra, razaoSocialEmpresa }.
function normalizeObra(item) {
  // AJUSTE BACKEND AQUI: ajuste aqui se a API de obras renomear campos de id/nome.
  return {
    idObra: String(item.id || item.idObra || item.id_obra || ''),
    nomeObra: String(item.nomeObra || item.nome_obra || item.nome || '-'),
    razaoSocialEmpresa: String(item.idEmpresaNavigation?.razaoSocial || item.empresa?.razaoSocial || '-')
  };
}

// Normaliza um responsável de moldagem para { id, nome } usado no select.
function normalizeRespMoldagem(item) {
  // AJUSTE BACKEND AQUI: ajuste aqui se a API de usuários/moldadores mudar os campos.
  return {
    idResponsavelMoldagem: String(item.idMoldador || item.id || item.id_moldador || item.id_responsavel_moldagem || ''),
    nomeResponsavelMoldagem: String(item.nomeMoldador || item.nome || item.nome_responsavel_moldagem || '-')
  };
}

// Normaliza um responsável de digitação para { id, nome } usado no select.
function normalizeRespDigitacao(item) {
  // AJUSTE BACKEND AQUI: ajuste aqui se a API de usuários/moldadores mudar os campos.
  return {
    idResponsavelDigitacao: String(item.idUsuario || item.id || item.id_responsavel_digitacao || ''),
    nomeResponsavelDigitacao: String(item.nomeCompleto || item.nome || item.nome_responsavel_digitacao || '-')
  };
}

// Tela de Concretagens realizadas: CRUD com obra/responsáveis, anexos de arquivos e cadastro rápido de moldador no modal.
function ConcretagensPage() {
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchInputRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [anexosSelecionados, setAnexosSelecionados] = useState([]);
  const [concretagens, setConcretagens] = useState([]);
  const [isAnexosModalOpen, setIsAnexosModalOpen] = useState(false);
  const [anexosModalItems, setAnexosModalItems] = useState([]);
  const [anexosModalTitle, setAnexosModalTitle] = useState('Anexos');
  const [anexosModalConcretagemId, setAnexosModalConcretagemId] = useState(null);
  const [abrindoAnexoId, setAbrindoAnexoId] = useState(null);
  const [baixandoAnexoId, setBaixandoAnexoId] = useState(null);
  const [baixandoTodosAnexos, setBaixandoTodosAnexos] = useState(false);
  // Confirmação de exclusão de anexo salvo (aberta por cima do modal de edição).
  const [anexoParaRemover, setAnexoParaRemover] = useState(null);
  const [previewAnexo, setPreviewAnexo] = useState({ status: 'carregando', url: null });
  const [removendoAnexo, setRemovendoAnexo] = useState(false);
  const anexoParaRemoverIdRef = useRef(null);
  const [viewConcretagem, setViewConcretagem] = useState(null);
  const [obras, setObras] = useState([]);
  const [moldadores, setMoldadores] = useState([]);
  const [digitadores, setDigitadores] = useState([]);
  const [activeModalTab, setActiveModalTab] = useState('concretagem');
  const [moldadorFormData, setMoldadorFormData] = useState(INITIAL_MOLDADOR_FORM);
  const [moldadorFormErrors, setMoldadorFormErrors] = useState({});

  // Carrega em paralelo concretagens, obras, moldadores e usuários (para listagem e selects).
  useEffect(() => {
    let isMounted = true;
    const loadInitialData = async () => {
      // AJUSTE BACKEND AQUI: esta tela depende destes endpoints para montar FKs e listagem.
      const [concretagensResult, obrasResult, moldadoresResult, usersResult] = await Promise.allSettled([
        getConcretagemRealizada(),
        getObras(),
        getMoldador(),
        getUser(),
      ]);

      if (!isMounted) return;

      if (concretagensResult.status === 'fulfilled' && Array.isArray(concretagensResult.value)) {
        setConcretagens(concretagensResult.value.map(normalizeConcretagem));
      }

      if (obrasResult.status === 'fulfilled' && Array.isArray(obrasResult.value)) {
        const normalizedObras = obrasResult.value.map(normalizeObra).filter(obra => obra.idObra);
        if (normalizedObras.length > 0) setObras(normalizedObras);
      }

      if (moldadoresResult.status === 'fulfilled' && Array.isArray(moldadoresResult.value)) {
        const normalizedMoldadores = moldadoresResult.value.map(normalizeRespMoldagem).filter(resp => resp.idResponsavelMoldagem);
        if (normalizedMoldadores.length > 0) setMoldadores(normalizedMoldadores);
      }

      if (usersResult.status === 'fulfilled' && Array.isArray(usersResult.value)) {
        const normalizedUsers = usersResult.value.map(normalizeRespDigitacao).filter(resp => resp.idResponsavelDigitacao);
        if (normalizedUsers.length > 0) setDigitadores(normalizedUsers);
      }
      setLoading(false);
    };

    loadInitialData().catch(() => {
      if (isMounted) setLoading(false);
    });

    return () => { isMounted = false; };
  }, []);

  // Foca o campo de busca assim que ele é aberto.
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen])

  // Índices id -> dados, para resolver nomes de obra/empresa/responsáveis na listagem.
  const obraMap = useMemo(() => obras.reduce((acc, obra) => {
    acc[String(obra.idObra)] = {
      nomeObra: obra.nomeObra,
      razaoSocialEmpresa: obra.razaoSocialEmpresa
    };
    return acc;
  }, {}), [obras]);
  const moldadorMap = useMemo(() => moldadores.reduce((acc, pessoa) => ({ ...acc, [String(pessoa.idResponsavelMoldagem)]: pessoa.nomeResponsavelMoldagem }), {}), [moldadores]);
  const digitadorMap = useMemo(() => digitadores.reduce((acc, pessoa) => ({ ...acc, [String(pessoa.idResponsavelDigitacao)]: pessoa.nomeResponsavelDigitacao }), {}), [digitadores]);

  const filteredConcretagens = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return concretagens;

    return concretagens.filter((item) => {
      const empresaNome = obraMap[String(item.idObra)]?.razaoSocialEmpresa || '';
      const obraNome = obraMap[String(item.idObra)]?.nomeObra || '';
      const data = item.dataConcretagem || '';
      const digitadorNome = item.nomeResponsavelDigitacao || digitadorMap[String(item.idResponsavelDigitacao)] || '';
      return (
        empresaNome.toLowerCase().includes(normalized) ||
        obraNome.toLowerCase().includes(normalized) ||
        data.toLowerCase().includes(normalized) ||
        digitadorNome.toLowerCase().includes(normalized)
      );
    });
  }, [query, concretagens, obraMap, digitadorMap]);

  const stats = useMemo(() => {
    const total = concretagens.length;
    const totalCaminhoes = concretagens.reduce((acc, item) => acc + (Number(item.qtdCaminhoes) || 0), 0);
    const totalCps = concretagens.reduce((acc, item) => acc + (Number(item.qtdTotalCp) || 0), 0);
    return { total, totalCaminhoes, totalCps };
  }, [concretagens]);

  // Handler de onChange por campo do formulário de concretagem.
  const handleFieldChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  // Abre/fecha o campo de busca, limpando a query ao fechar.
  const handleToggleSearch = () => {
    setIsSearchOpen((prev) => {
      const willOpen = !prev;
      if (!willOpen) {
        setQuery('');
      }
      return willOpen;
    });
  };

  // Abre o modal em modo de criação (reseta formulário, anexos, aba e form de moldador).
  const openModal = () => {
    setEditingId(null);
    setFormData(INITIAL_FORM);
    setAnexosSelecionados([]);
    setFormErrors({});
    setActiveModalTab('concretagem');
    setMoldadorFormData(INITIAL_MOLDADOR_FORM);
    setMoldadorFormErrors({});
    setIsModalOpen(true);
  };

  // Abre o modal em modo de edição, preenchendo o formulário com os dados da concretagem.
  const openEditModal = (item) => {
    setEditingId(item.id);
    setFormData({
      idObra: String(item.idObra || ''),
      dataConcretagem: normalizeDateTimeLocal(item.dataConcretagem || ''),
      qtdCaminhoes: String(item.qtdCaminhoes || ''),
      qtdTotalCp: String(item.qtdTotalCp || ''),
      idResponsavelMoldagem: String(item.idResponsavelMoldagem || ''),
      idResponsavelDigitacao: String(item.idResponsavelDigitacao || ''),
    });
    setAnexosSelecionados([]);
    setFormErrors({});
    setActiveModalTab('concretagem');
    setMoldadorFormData(INITIAL_MOLDADOR_FORM);
    setMoldadorFormErrors({});
    setIsModalOpen(true);
  };

  // Fecha o modal e limpa formulário, anexos, aba e form de moldador.
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setFormErrors({});
    setAnexosSelecionados([]);
    setActiveModalTab('concretagem');
    setMoldadorFormData(INITIAL_MOLDADOR_FORM);
    setMoldadorFormErrors({});
  };

  // Handler de onChange dos campos do form de moldador (aba do modal).
  const handleMoldadorFieldChange = (field) => (event) => {
    setMoldadorFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  // Handler de onChange com máscara (CPF/telefone) no form de moldador.
  const handleMoldadorMaskedChange = (field, formatter) => (event) => {
    setMoldadorFormData((prev) => ({ ...prev, [field]: formatter(event.target.value) }));
  };

  // Valida e cria um moldador pela aba do modal, adicionando-o ao select de responsável.
  const handleAddMoldador = async () => {
    const errors = {};
    if (!moldadorFormData.nome.trim()) errors.nome = 'Nome obrigatório.';

    const phoneDigits = onlyDigits(moldadorFormData.telefone);
    if (!phoneDigits) errors.telefone = 'Telefone obrigatório.';
    else if (phoneDigits.length < 10 || phoneDigits.length > 11) errors.telefone = 'Telefone inválido.';

    // Email opcional: valida o formato apenas quando preenchido.
    if (moldadorFormData.email.trim() && !/\S+@\S+\.\S+/.test(moldadorFormData.email.trim())) errors.email = 'Email inválido.';

    if (Object.keys(errors).length > 0) {
      setMoldadorFormErrors(errors);
      return;
    }

    try {
      const { response: created, payload } = await createMoldador({
        nome: moldadorFormData.nome.trim(),
        cpf: moldadorFormData.cpf,
        telefone: phoneDigits,
        email: moldadorFormData.email.trim(),
        ativo: true,
      });

      const novoMoldador = normalizeRespMoldagem(
        created || {
          idMoldador: `mol-local-${Date.now()}`,
          nomeMoldador: moldadorFormData.nome.trim(),
          ...payload,
        }
      );

      setMoldadores((prev) => [novoMoldador, ...prev]);
      setMoldadorFormData(INITIAL_MOLDADOR_FORM);
      setMoldadorFormErrors({});
      showSuccessToast('Moldador cadastrado com sucesso.');
      setActiveModalTab('concretagem');
    } catch (error) {
      setMoldadorFormErrors({ geral: error?.message || 'Não foi possível salvar o moldador.' });
    }
  };

  // Seleciona anexos (imagem/PDF), valida o tipo e remove duplicados por nome+tamanho+data.
  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    const invalidFile = files.find((file) => !file.type.startsWith('image/') && file.type !== 'application/pdf');

    if (invalidFile) {
      setFormErrors((prev) => ({ ...prev, anexos: 'Somente arquivos de imagem ou PDF são permitidos.' }));
      return;
    }

    setFormErrors((prev) => {
      const { anexos, ...rest } = prev;
      return rest;
    });

    setAnexosSelecionados((prev) => {
      const merged = [...prev, ...files];
      const uniqueBySignature = new Map();
      merged.forEach((file) => {
        const signature = `${file.name}-${file.size}-${file.lastModified}`;
        if (!uniqueBySignature.has(signature)) {
          uniqueBySignature.set(signature, file);
        }
      });
      return Array.from(uniqueBySignature.values());
    });

    event.target.value = '';
  };

  // Indica se já existe outra concretagem da mesma obra na mesma data e hora.
  const isConcretagemDuplicada = (idObra, dataConcretagem) => {
    const dataNormalizada = normalizeDateTimeLocal(dataConcretagem);
    if (!idObra || !dataNormalizada) return false;

    return concretagens.some((item) => (
      item.id !== editingId &&
      String(item.idObra) === String(idObra) &&
      normalizeDateTimeLocal(item.dataConcretagem) === dataNormalizada
    ));
  };

  // Valida obra, data, responsável de digitação e quantidades (caminhões e CP) dentro dos limites.
  const validateForm = () => {
    const errors = {};
    if (!formData.idObra) errors.idObra = 'Obra obrigatório.';
    if (!formData.dataConcretagem) errors.dataConcretagem = 'Data da concretagem obrigatória.';
    else if (isConcretagemDuplicada(formData.idObra, formData.dataConcretagem)) errors.dataConcretagem = 'Já existe uma concretagem cadastrada para esta obra com a mesma data e hora.';
    if (!formData.idResponsavelDigitacao) errors.idResponsavelDigitacao = 'Responsável pela digitação obrigatório.';

    const qtdCaminhoes = Number(formData.qtdCaminhoes);
    if (!formData.qtdCaminhoes) errors.qtdCaminhoes = 'Quantidade de caminhões obrigatória.';
    else if (!Number.isInteger(qtdCaminhoes) || qtdCaminhoes < 1 || qtdCaminhoes > 999) errors.qtdCaminhoes = 'Quantidade de caminhões deve ser inteiro entre 1 e 999.';

    const qtdTotalCp = Number(formData.qtdTotalCp);
    if (!formData.qtdTotalCp) errors.qtdTotalCp = 'Quantidade total de CP obrigatória.';
    else if (!Number.isInteger(qtdTotalCp) || qtdTotalCp < 1 || qtdTotalCp > 9999) errors.qtdTotalCp = 'Quantidade total de CP deve ser inteiro entre 1 e 9999.';

    return { errors };
  };

  // Submete o form: valida, cria/atualiza na API (com anexos) e completa os nomes de FK na lista local.
  const [submitting, handleSave] = useSubmitLock(async (event) => {
    event.preventDefault();
    const { errors } = validateForm();

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      if (editingId) {
        await updateConcretagemRealizada(editingId, formData);

        // O PUT grava só os dados: arquivos novos sobem em seguida pela rota de anexos.
        let anexosNovos = [];
        let erroAnexos = null;
        if (anexosSelecionados.length > 0) {
          try {
            const criados = await uploadAnexosConcretagem(editingId, anexosSelecionados);
            anexosNovos = Array.isArray(criados) ? criados : [];
          } catch (error) {
            erroAnexos = error;
          }
        }

        setConcretagens((prev) => prev.map((item) => {
          if (item.id === editingId) {
            return {
              ...item,
              idObra: formData.idObra,
              nomeObra: obraMap[String(formData.idObra)]?.nomeObra || item.nomeObra || '-',
              razaoSocialEmpresa: obraMap[String(formData.idObra)]?.razaoSocialEmpresa || item.razaoSocialEmpresa || '-',
              dataConcretagem: formData.dataConcretagem,
              qtdCaminhoes: formData.qtdCaminhoes,
              qtdTotalCp: formData.qtdTotalCp,
              idResponsavelMoldagem: formData.idResponsavelMoldagem,
              nomeResponsavelMoldagem: moldadorMap[String(formData.idResponsavelMoldagem)] || item.nomeResponsavelMoldagem || '-',
              idResponsavelDigitacao: formData.idResponsavelDigitacao,
              nomeResponsavelDigitacao: digitadorMap[String(formData.idResponsavelDigitacao)] || item.nomeResponsavelDigitacao || '-',
              anexos: [...(item.anexos || []), ...anexosNovos],
              qtdAnexos: (Number(item.qtdAnexos) || 0) + anexosNovos.length
            };
          }
          return item;
        }));

        // Dados já gravados: mantém o modal aberto com os arquivos selecionados para tentar de novo.
        if (erroAnexos) {
          setFormErrors({ geral: `Os dados foram salvos, mas não foi possível enviar os anexos${erroAnexos.message ? `: ${erroAnexos.message}` : '.'}` });
          return;
        }
        showSuccessToast('Concretagem realizada atualizada com sucesso.');
      } else {
        const { response: created, payload } = await createConcretagemRealizada(formData, anexosSelecionados);

        let normalizedCreated;
        if (created && created.idConcretagemRealizada) {
          normalizedCreated = normalizeConcretagem(created);
          if (!normalizedCreated.nomeObra || normalizedCreated.nomeObra === '-') {
            normalizedCreated.nomeObra = obraMap[String(normalizedCreated.idObra)]?.nomeObra || '-';
          }
          if (!normalizedCreated.razaoSocialEmpresa || normalizedCreated.razaoSocialEmpresa === '-') {
            normalizedCreated.razaoSocialEmpresa = obraMap[String(normalizedCreated.idObra)]?.razaoSocialEmpresa || '-';
          }
          if (!normalizedCreated.nomeResponsavelDigitacao || normalizedCreated.nomeResponsavelDigitacao === '-') {
            normalizedCreated.nomeResponsavelDigitacao = digitadorMap[String(normalizedCreated.idResponsavelDigitacao)] || '-';
          }
          if (!normalizedCreated.nomeResponsavelMoldagem || normalizedCreated.nomeResponsavelMoldagem === '-') {
            normalizedCreated.nomeResponsavelMoldagem = moldadorMap[String(normalizedCreated.idResponsavelMoldagem)] || '-';
          }
        } else {
          normalizedCreated = {
            id: created?.idConcretagemRealizada || `conc-${Date.now()}`,
            idObra: formData.idObra,
            nomeObra: obraMap[String(formData.idObra)]?.nomeObra || '-',
            razaoSocialEmpresa: obraMap[String(formData.idObra)]?.razaoSocialEmpresa || '-',
            dataConcretagem: formData.dataConcretagem,
            qtdCaminhoes: formData.qtdCaminhoes,
            qtdTotalCp: formData.qtdTotalCp,
            idResponsavelMoldagem: formData.idResponsavelMoldagem,
            nomeResponsavelMoldagem: moldadorMap[String(formData.idResponsavelMoldagem)] || '-',
            idResponsavelDigitacao: formData.idResponsavelDigitacao,
            nomeResponsavelDigitacao: digitadorMap[String(formData.idResponsavelDigitacao)] || '-',
            anexos: payload.anexos || []
          };
        }
        setConcretagens((prev) => [normalizedCreated, ...prev]);
        showSuccessToast('Concretagem realizada cadastrada com sucesso.');
      }
      closeModal();
    } catch (error) {
      setFormErrors({ geral: error?.message || 'Não foi possível salvar a concretagem.' });
    }
  });

  // Exclui a concretagem após confirmação e remove da lista local.
  const handleDelete = async (id) => {
    const confirm = window.confirm("Deseja realmente excluir esta concretagem?");
    if (!confirm) return;

    try {
      await deleteConcretagemRealizada(id);
      setConcretagens((prev) => prev.filter((item) => item.id !== id));
      showSuccessToast('Concretagem excluída com sucesso.');
    } catch (error) {
      showErrorToast(error?.message || 'Não foi possível excluir a concretagem.');
    }
  };

  // Extrai/normaliza a lista de anexos de uma concretagem (com fallback por quantidade) para o modal.
  const extractAnexosFromItem = (item) => {
    // AJUSTE BACKEND AQUI: este método é o ponto principal para conectar o modal de anexos
    // com a tabela arquivo_concretagem. Quando o back devolver os anexos da concretagem,
    // basta mapear aqui os campos reais (nome original, URL no Azure e metadata).
    if (!item) return [];

    if (Array.isArray(item.anexos) && item.anexos.length > 0) {
      return item.anexos.map((a) => ({ id: a.idArquivoConcretagem || a.id || null, nome: a.nomeOriginal || a.fileName || 'arquivo', tipo: a.tipo || a.contentType || '-', tamanho: a.tamanho || a.size || 0 }));
    }

    if (Array.isArray(item.arquivosConcretagens) && item.arquivosConcretagens.length > 0) {
      // AJUSTE BACKEND AQUI: se o payload vier direto da tabela arquivo_concretagem,
      // troque os aliases abaixo pelos nomes reais do contrato do back.
      return item.arquivosConcretagens.map((a) => ({ id: a.idArquivoConcretagem || a.id || null, nome: a.nome || a.nomeArquivo || a.fileName || 'arquivo', tipo: a.tipo || a.contentType || '-', tamanho: a.tamanho || a.size || 0 }));
    }

    // Fallback temporário: se o back ainda enviar só a quantidade, mostramos placeholders.
    // Quando a integração estiver pronta, este bloco pode ser removido.
    if (Number(item.qtdAnexos) > 0) {
      return Array.from({ length: Number(item.qtdAnexos) }).map((_, idx) => ({ id: null, nome: `Anexo ${idx + 1}`, tipo: '-', tamanho: 0 }));
    }

    return [];
  };

  // Abre o modal de anexos para uma concretagem, com título por obra/empresa e data.
  const openAnexosModal = (item) => {
    const anexos = extractAnexosFromItem(item || {});
    setAnexosModalItems(anexos);
    setAnexosModalConcretagemId(item?.id ?? null);
    setAnexosModalTitle(
      `${item?.nomeObra || item?.razaoSocialEmpresa || 'Concretagem'} | ${formatDateTimeDisplay(item?.dataConcretagem)}`
    );
    setIsAnexosModalOpen(true);
  };

  // Fecha o modal de anexos.
  const closeAnexosModal = () => {
    setIsAnexosModalOpen(false);
    setAnexosModalItems([]);
    setAnexosModalTitle('Anexos');
    setAnexosModalConcretagemId(null);
  };

  // Gera uma URL SAS de curta duração e só então abre o anexo — nunca expõe a URL pública fixa do blob.
  const handleAbrirAnexo = async (idArquivo) => {
    if (!anexosModalConcretagemId || !idArquivo) return;
    setAbrindoAnexoId(idArquivo);
    try {
      const { url } = await getAnexoDownloadUrl(anexosModalConcretagemId, idArquivo);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      showErrorToast(error?.message || 'Não foi possível abrir o anexo.');
    } finally {
      setAbrindoAnexoId(null);
    }
  };

  // Busca o conteúdo de um anexo pela URL SAS de curta duração.
  const buscarAnexo = async (anexo) => {
    const { url } = await getAnexoDownloadUrl(anexosModalConcretagemId, anexo.id);
    const resposta = await fetch(url).catch(() => null);
    if (!resposta?.ok) throw new Error(`Não foi possível baixar o anexo "${anexo.nome}".`);
    return resposta;
  };

  // Baixa um único anexo com o nome original, sem abrir em outra aba.
  const handleBaixarAnexo = async (anexo) => {
    if (!anexosModalConcretagemId || !anexo.id) return;
    setBaixandoAnexoId(anexo.id);
    try {
      const resposta = await buscarAnexo(anexo);
      salvarArquivo(await resposta.blob(), anexo.nome);
    } catch (error) {
      showErrorToast(error?.message || 'Não foi possível baixar o anexo.');
    } finally {
      setBaixandoAnexoId(null);
    }
  };

  // Só os anexos com id têm arquivo no back (os placeholders por quantidade não têm).
  const anexosBaixaveis = anexosModalItems.filter((a) => a.id);

  // Baixa todos os anexos da concretagem em um único .zip.
  const handleBaixarTodosAnexos = async () => {
    if (!anexosModalConcretagemId || anexosBaixaveis.length === 0) return;
    setBaixandoTodosAnexos(true);
    try {
      const conteudos = await Promise.all(anexosBaixaveis.map(async (anexo) => {
        const resposta = await buscarAnexo(anexo);
        return new Uint8Array(await resposta.arrayBuffer());
      }));
      const arquivos = {};
      anexosBaixaveis.forEach((anexo, i) => {
        arquivos[nomeLivreNoZip(arquivos, anexo.nome)] = conteudos[i];
      });
      // Imagens e PDF já são comprimidos: level 0 só empacota, sem recomprimir.
      const zip = new Blob([zipSync(arquivos, { level: 0 })], { type: 'application/zip' });
      salvarArquivo(zip, `Anexos - ${anexosModalTitle.replace(/[\\/:*?"<>|]+/g, '-')}.zip`);
    } catch (error) {
      showErrorToast(error?.message || 'Não foi possível baixar os anexos.');
    } finally {
      setBaixandoTodosAnexos(false);
    }
  };

  // Anexos já salvos da concretagem em edição (só os com id existem no back e podem ser excluídos).
  const anexosSalvos = editingId
    ? extractAnexosFromItem(concretagens.find((item) => item.id === editingId)).filter((a) => a.id)
    : [];

  // Tira da seleção um arquivo ainda não enviado.
  const handleRemoverAnexoSelecionado = (file) => {
    setAnexosSelecionados((prev) => prev.filter((item) => item !== file));
  };

  // Abre a confirmação de exclusão e busca a URL SAS para a pré-visualização do anexo.
  const abrirConfirmacaoRemoverAnexo = async (anexo) => {
    anexoParaRemoverIdRef.current = anexo.id;
    setAnexoParaRemover(anexo);
    setPreviewAnexo({ status: 'carregando', url: null });
    try {
      const { url } = await getAnexoDownloadUrl(editingId, anexo.id);
      // Ignora a resposta se a confirmação já foi fechada ou aberta para outro anexo.
      if (anexoParaRemoverIdRef.current !== anexo.id) return;
      setPreviewAnexo(url ? { status: 'pronto', url } : { status: 'indisponivel', url: null });
    } catch (error) {
      if (anexoParaRemoverIdRef.current !== anexo.id) return;
      setPreviewAnexo({ status: 'indisponivel', url: null });
    }
  };

  // Fecha a confirmação de exclusão de anexo.
  const fecharConfirmacaoRemoverAnexo = () => {
    anexoParaRemoverIdRef.current = null;
    setAnexoParaRemover(null);
    setPreviewAnexo({ status: 'carregando', url: null });
  };

  // Exclui o anexo confirmado e atualiza a lista e a contagem local da concretagem.
  const handleConfirmarRemoverAnexo = async () => {
    if (!editingId || !anexoParaRemover?.id) return;
    const idAnexo = anexoParaRemover.id;
    setRemovendoAnexo(true);
    try {
      await deleteAnexoConcretagem(editingId, idAnexo);
      setConcretagens((prev) => prev.map((item) => {
        if (item.id !== editingId) return item;
        return {
          ...item,
          anexos: (item.anexos || []).filter((a) => String(a.idArquivoConcretagem || a.id) !== String(idAnexo)),
          qtdAnexos: Math.max(0, (Number(item.qtdAnexos) || 0) - 1),
        };
      }));
      showSuccessToast('Anexo excluído com sucesso.');
      fecharConfirmacaoRemoverAnexo();
    } catch (error) {
      showErrorToast(error?.message || 'Não foi possível excluir o anexo.');
    } finally {
      setRemovendoAnexo(false);
    }
  };

  return (
    < div className="concretagens-page space-y-6" >
      <section className="concretagens-header flex flex-col gap-4 rounded-2xl bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 sm:text-3xl">Concretagens realizadas</h2>
          <p className="mt-2 max-w-2xl text-sm text-slate-500">
            Visualize e gerencie as concretagens cadastradas no sistema
          </p>
        </div>
        <button
          type="button"
          onClick={openModal}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-blue-200 hover:text-blue-600 sm:w-auto"
        >
          <Plus className="h-4 w-4" />
          Nova concretagem
        </button>
      </section>

      {isAnexosModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-4">
          <button type="button" className="absolute inset-0 bg-slate-900/40" onClick={closeAnexosModal} aria-label="Fechar" />
          <div className="relative z-10 w-full max-w-2xl max-h-[80vh] overflow-y-auto space-y-4 rounded-2xl bg-white p-4 shadow-xl">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">{anexosModalTitle}</h3>
                <p className="text-xs text-slate-500">Anexos fixados para esta concretagem</p>
              </div>
              <button type="button" onClick={closeAnexosModal} className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-700" aria-label="Fechar">
                <X className="h-4 w-4" />
              </button>
            </div>

            {anexosModalItems.length === 0 ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">Nenhum anexo encontrado.</div>
            ) : (
              <div className="space-y-2">
                {anexosModalItems.map((a, idx) => (
                  <div key={`${a.nome}-${idx}`} className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 p-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <Paperclip className="h-5 w-5 flex-shrink-0 text-slate-500" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate max-w-md">{a.nome}</div>
                        <div className="text-xs text-slate-500">{a.tipo} • {formatFileSize(a.tamanho)}</div>
                      </div>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-2">
                      {a.id ? (
                        <>
                          <button
                            type="button"
                            onClick={() => handleAbrirAnexo(a.id)}
                            disabled={abrindoAnexoId === a.id}
                            className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-[#0e7490]/40 hover:text-[#0e7490] disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                            {abrindoAnexoId === a.id ? 'Abrindo...' : 'Abrir'}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleBaixarAnexo(a)}
                            disabled={baixandoAnexoId === a.id}
                            className="inline-flex items-center gap-1 rounded-full border border-transparent bg-[#0e7490]/10 px-2.5 py-1 text-xs font-medium text-[#0e7490] transition hover:bg-[#0e7490]/20 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <Download className="h-3.5 w-3.5" />
                            {baixandoAnexoId === a.id ? 'Baixando...' : 'Baixar'}
                          </button>
                        </>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {anexosBaixaveis.length > 1 ? (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleBaixarTodosAnexos}
                  disabled={baixandoTodosAnexos}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 shadow-sm transition hover:border-[#0e7490]/40 hover:text-[#0e7490] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Download className="h-3.5 w-3.5" />
                  {baixandoTodosAnexos ? 'Baixando...' : 'Baixar todos'}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {anexoParaRemover ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-4">
          <button type="button" className="absolute inset-0 bg-slate-900/40" onClick={fecharConfirmacaoRemoverAnexo} disabled={removendoAnexo} aria-label="Fechar" />
          <div className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto space-y-4 rounded-2xl bg-white p-4 shadow-xl sm:p-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Tem certeza que deseja excluir este anexo?</h3>
              <p className="mt-1 break-all text-sm text-slate-600">{anexoParaRemover.nome}</p>
            </div>

            <div className="flex min-h-[10rem] items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
              {previewAnexo.status === 'carregando' ? (
                <p className="p-4 text-sm text-slate-500">Carregando pré-visualização...</p>
              ) : previewAnexo.status === 'indisponivel' ? (
                <p className="p-4 text-sm text-slate-500">Pré-visualização indisponível.</p>
              ) : /\.pdf$/i.test(anexoParaRemover.nome) ? (
                <iframe src={previewAnexo.url} title={`Pré-visualização de ${anexoParaRemover.nome}`} className="h-80 w-full" />
              ) : (
                // O upload só aceita imagem ou PDF; se a imagem não carregar, mantém a URL para o link abaixo.
                <img
                  src={previewAnexo.url}
                  alt={anexoParaRemover.nome}
                  onError={() => setPreviewAnexo((prev) => ({ ...prev, status: 'indisponivel' }))}
                  className="max-h-80 w-full object-contain"
                />
              )}
            </div>

            {previewAnexo.url ? (
              <a
                href={previewAnexo.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-[#0e7490] hover:underline"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Abrir em nova aba
              </a>
            ) : null}

            <p className="text-xs text-slate-500">Essa ação não pode ser desfeita.</p>

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
              <button
                type="button"
                onClick={fecharConfirmacaoRemoverAnexo}
                disabled={removendoAnexo}
                className="w-full rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmarRemoverAnexo}
                disabled={removendoAnexo}
                className="w-full rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {removendoAnexo ? 'Excluindo...' : 'Excluir anexo'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <section className="concretagens-metrics grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Total</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.total}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Caminhões</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.totalCaminhoes}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">CP total</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.totalCps}</p>
        </div>
      </section>

      <section className="concretagens-table overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 sm:hidden">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleToggleSearch}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:border-blue-200 hover:text-blue-600"
              aria-label={isSearchOpen ? 'Fechar pesquisa' : 'Abrir pesquisa'}
              title={isSearchOpen ? 'Fechar pesquisa' : 'Abrir pesquisa'}
            >
              <Search className="h-4 w-4" />
            </button>
            <div
              className={`overflow-hidden transition-all duration-200 ${isSearchOpen ? 'max-w-xs opacity-100' : 'max-w-0 opacity-0'
                }`}
            >
              <input
                type="text"
                placeholder="Buscar por obra, empresa, data ou responsável"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                className="w-56 rounded-full border border-slate-200 bg-slate-50 py-2 pl-3 pr-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 sm:w-72"
              />
            </div>
          </div>
          <span className="text-xs text-slate-500">Resultados: {filteredConcretagens.length}</span>
        </div>
        <div className="hidden overflow-x-auto sm:block">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-[0.3em] text-slate-400">
              <tr>
                <th className="px-4 py-3">Empresa</th>
                <th className="px-4 py-3">Obra</th>
                <th className="px-4 py-3">Data</th>
                <th className="px-4 py-3">Qt. caminhões</th>
                <th className="px-4 py-3">Qt. CP</th>
                {/*<th className="px-4 py-3">Resp. moldagem</th>*/}
                <th className="px-4 py-3">Resp. digitação</th>
                <th className="px-4 py-3">Anexos</th>
                <th className="px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <span>Ações</span>
                    <div className="flex items-center gap-2 normal-case tracking-normal">
                      <button
                        type="button"
                        onClick={handleToggleSearch}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-blue-200 hover:text-blue-600"
                        aria-label={isSearchOpen ? 'Fechar pesquisa' : 'Abrir pesquisa'}
                        title={isSearchOpen ? 'Fechar pesquisa' : 'Abrir pesquisa'}
                      >
                        <Search className="h-4 w-4" />
                      </button>
                      <div
                        className={`overflow-hidden transition-all duration-200 ${isSearchOpen ? 'max-w-xs opacity-100' : 'max-w-0 opacity-0'
                          }`}
                      >
                        <input
                          ref={searchInputRef}
                          type="text"
                          placeholder="Buscar..."
                          value={query}
                          onChange={(event) => setQuery(event.target.value)}
                          className="w-56 rounded-full border border-slate-200 bg-white py-1.5 pl-3 pr-3 text-xs font-medium text-slate-700 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        />
                      </div>
                      <span className="whitespace-nowrap text-xs font-medium text-slate-500">
                        Resultados: {filteredConcretagens.length}
                      </span>
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={`sk-${idx}`} className="border-b border-slate-100">
                    {Array.from({ length: 8 }).map((__, cell) => (
                      <td key={cell} className="px-4 py-4">
                        <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filteredConcretagens.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center text-sm text-slate-500">
                    Nenhuma concretagem encontrada.
                  </td>
                </tr>
              ) : (
                filteredConcretagens.map((item) => (
                  <tr key={item.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/80">
                    <td className="px-4 py-4 text-slate-700 font-medium">{item.razaoSocialEmpresa || '-'}</td>
                    <td className="px-4 py-4 font-medium text-slate-900">{item.nomeObra || '-'}</td>
                    <td className="px-4 py-4 text-slate-600">{formatDateTimeDisplay(item.dataConcretagem)}</td>
                    <td className="px-4 py-4 text-slate-600">{item.qtdCaminhoes}</td>
                    <td className="px-4 py-4 text-slate-600">{item.qtdTotalCp}</td>
                    {/*<td className="px-4 py-4 text-slate-600">
                      {moldadorMap[String(item.idResponsavelMoldagem)] || '-'}
                    </td>*/}
                    <td className="px-4 py-4 text-slate-600">{item.nomeResponsavelDigitacao || '-'}</td>
                    <td className="px-4 py-4 text-slate-600">
                      <button
                        type="button"
                        onClick={() => openAnexosModal(item)}
                        className="inline-flex items-center gap-2 text-xs text-slate-600 hover:text-blue-600"
                      >
                        <Paperclip className="h-4 w-4 text-slate-500" />
                        <span>{item.qtdAnexos || 0}</span>
                      </button>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setViewConcretagem(item)}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-[#0e7490]/40 hover:text-[#0e7490]"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          Visualizar
                        </button>
                        <button
                          type="button"
                          onClick={() => openEditModal(item)}
                          className="inline-flex items-center rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600"
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(item.id)}
                          className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-1 text-xs font-medium text-red-600 transition hover:border-red-300 hover:bg-red-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-3 p-4 sm:hidden">
          {loading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <div key={`mobile-sk-${index}`} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="h-4 w-1/2 animate-pulse rounded bg-slate-100" />
                <div className="mt-3 h-3 w-2/3 animate-pulse rounded bg-slate-100" />
              </div>
            ))
          ) : filteredConcretagens.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
              Nenhuma concretagem encontrada.
            </div>
          ) : (
            filteredConcretagens.map((item) => (
              <div key={item.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-base font-semibold text-slate-900">{item.razaoSocialEmpresa || '-'}</p>
                <p className="mt-1 text-xs font-medium text-slate-600">{item.nomeObra || '-'}</p>
                <p className="mt-1 text-xs text-slate-500">Data: {formatDateTimeDisplay(item.dataConcretagem)}</p>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600">
                  <span>Caminhões: {item.qtdCaminhoes}</span>
                  <span>CP: {item.qtdTotalCp}</span>
                  <span className="col-span-2">Anexos: <button type="button" onClick={() => openAnexosModal(item)} className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-blue-600"><Paperclip className="h-3.5 w-3.5 text-slate-500" /> {item.qtdAnexos || 0}</button></span>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setViewConcretagem(item)}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-[#0e7490]/40 hover:text-[#0e7490]"
                  >
                    <Eye className="h-3.5 w-3.5" />
                    Visualizar
                  </button>
                  <button
                    type="button"
                    onClick={() => openEditModal(item)}
                    className="inline-flex items-center rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600"
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(item.id)}
                    className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-1 text-xs font-medium text-red-600 transition hover:border-red-300 hover:bg-red-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Excluir
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <RecordViewModal
        open={!!viewConcretagem}
        onClose={() => setViewConcretagem(null)}
        title="Detalhes da concretagem"
        subtitle="Dados registrados — as imagens ficam disponíveis em Anexos."
        sections={viewConcretagem ? [
          {
            title: 'Dados gerais',
            fields: [
              { label: 'Empresa', value: viewConcretagem.razaoSocialEmpresa || obraMap[String(viewConcretagem.idObra)]?.razaoSocialEmpresa || '-' },
              { label: 'Obra', value: viewConcretagem.nomeObra || obraMap[String(viewConcretagem.idObra)]?.nomeObra || '-' },
              { label: 'Data de concretagem', value: formatDateTimeDisplay(viewConcretagem.dataConcretagem) },
              { label: 'Qtd. de caminhões', value: viewConcretagem.qtdCaminhoes ?? '-' },
              { label: 'Qtd. total de CP', value: viewConcretagem.qtdTotalCp ?? '-' },
            ],
          },
          {
            title: 'Responsáveis',
            fields: [
              { label: 'Responsável moldagem', value: viewConcretagem.nomeResponsavelMoldagem || moldadorMap[String(viewConcretagem.idResponsavelMoldagem)] || '-' },
              { label: 'Responsável digitação', value: viewConcretagem.nomeResponsavelDigitacao || digitadorMap[String(viewConcretagem.idResponsavelDigitacao)] || '-' },
            ],
          },
          {
            title: 'Anexos',
            fields: [
              { label: 'Quantidade de anexos', value: viewConcretagem.qtdAnexos || 0 },
            ],
          },
        ] : []}
      />

      {
        isModalOpen ? (
          <div className="fixed inset-0 z-40 flex items-center justify-center px-4 py-4 sm:py-0">
            <button
              type="button"
              className="absolute inset-0 bg-slate-900/40"
              onClick={closeModal}
              aria-label="Fechar modal"
            />
            <form
              onSubmit={handleSave}
              className="relative z-10 w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-4 shadow-xl sm:p-6"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Cadastro</p>
                  <h3 className="text-xl font-semibold text-slate-900">
                    {editingId ? 'Editar concretagem' : 'Nova concretagem'}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={closeModal}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                  aria-label="Fechar"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {formErrors.geral ? (
                <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {formErrors.geral}
                </div>
              ) : null}

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setActiveModalTab('concretagem')}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${activeModalTab === 'concretagem'
                    ? 'border-[#0e7490] bg-[#0e7490]/10 text-[#0e7490]'
                    : 'border-slate-200 text-slate-600 hover:border-blue-200 hover:text-blue-600'
                    }`}
                >
                  Dados da concretagem
                </button>
                <button
                  type="button"
                  onClick={() => setActiveModalTab('moldador')}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${activeModalTab === 'moldador'
                    ? 'border-[#0e7490] bg-[#0e7490]/10 text-[#0e7490]'
                    : 'border-slate-200 text-slate-600 hover:border-blue-200 hover:text-blue-600'
                    }`}
                >
                  Cadastrar moldador
                </button>
              </div>

              {activeModalTab === 'moldador' ? (
                <div className="mt-6 space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2 sm:col-span-2">
                      <label className="text-sm font-medium text-slate-700">Nome completo <span className="text-rose-500">*</span></label>
                      <input
                        type="text"
                        value={moldadorFormData.nome}
                        onChange={handleMoldadorFieldChange('nome')}
                        className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                      />
                      {moldadorFormErrors.nome ? <p className="text-xs text-rose-600">{moldadorFormErrors.nome}</p> : null}
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">Telefone <span className="text-rose-500">*</span></label>
                      <input
                        type="text"
                        value={moldadorFormData.telefone}
                        onChange={handleMoldadorMaskedChange('telefone', formatPhone)}
                        className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                      />
                      {moldadorFormErrors.telefone ? <p className="text-xs text-rose-600">{moldadorFormErrors.telefone}</p> : null}
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">CPF:</label>
                      <input
                        type="text"
                        value={moldadorFormData.cpf}
                        onChange={handleMoldadorMaskedChange('cpf', formatCpf)}
                        className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                      />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <label className="text-sm font-medium text-slate-700">Email</label>
                      <input
                        type="email"
                        value={moldadorFormData.email}
                        onChange={handleMoldadorFieldChange('email')}
                        className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                      />
                      {moldadorFormErrors.email ? <p className="text-xs text-rose-600">{moldadorFormErrors.email}</p> : null}
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                    O moldador cadastrado ficará disponível imediatamente no campo "Responsável moldagem".
                  </div>
                  {moldadorFormErrors.geral ? (
                    <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm font-medium text-rose-600">
                      {moldadorFormErrors.geral}
                    </div>
                  ) : null}
                  <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                    <button
                      type="button"
                      onClick={() => setActiveModalTab('concretagem')}
                      className="w-full rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600 sm:w-auto"
                    >
                      Voltar para concretagem
                    </button>
                    <button
                      type="button"
                      onClick={handleAddMoldador}
                      className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 sm:w-auto"
                    >
                      Salvar moldador
                    </button>
                  </div>
                </div>
              ) : null}

              {activeModalTab === 'concretagem' ? (
                <>
                  <div className="mt-6 grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2 sm:col-span-2">
                      <label className="text-sm font-medium text-slate-700">Obra <span className="text-rose-500">*</span></label>
                      <select
                        value={formData.idObra}
                        onChange={handleFieldChange('idObra')}
                        className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                      >
                        <option value="">Selecione</option>
                        {obras.map((obra) => (
                          <option key={obra.idObra} value={obra.idObra}>
                            {obra.nomeObra}
                          </option>
                        ))}
                      </select>
                      {formErrors.idObra ? <p className="text-xs text-rose-600">{formErrors.idObra}</p> : null}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">Data de concretagem <span className="text-rose-500">*</span></label>
                      <input
                        type="datetime-local"
                        value={formData.dataConcretagem}
                        onChange={handleFieldChange('dataConcretagem')}
                        className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                      />
                      {formErrors.dataConcretagem ? (
                        <p className="text-xs text-rose-600">{formErrors.dataConcretagem}</p>
                      ) : null}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">Qtd. caminhões <span className="text-rose-500">*</span></label>
                      <input
                        type="number"
                        min="1"
                        max="999"
                        value={formData.qtdCaminhoes}
                        onChange={handleFieldChange('qtdCaminhoes')}
                        className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                      />
                      {formErrors.qtdCaminhoes ? (
                        <p className="text-xs text-rose-600">{formErrors.qtdCaminhoes}</p>
                      ) : null}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">Qtd. total de CP <span className="text-rose-500">*</span></label>
                      <input
                        type="number"
                        min="1"
                        max="9999"
                        value={formData.qtdTotalCp}
                        onChange={handleFieldChange('qtdTotalCp')}
                        className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                      />
                      {formErrors.qtdTotalCp ? (
                        <p className="text-xs text-rose-600">{formErrors.qtdTotalCp}</p>
                      ) : null}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">Responsável moldagem:</label>
                      <select
                        value={formData.idResponsavelMoldagem}
                        onChange={handleFieldChange('idResponsavelMoldagem')}
                        className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                      >
                        <option value="">Selecione</option>
                        {moldadores.map((item) => (
                          <option key={item.idResponsavelMoldagem} value={item.idResponsavelMoldagem}>
                            {item.nomeResponsavelMoldagem}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">Responsável digitação <span className="text-rose-500">*</span></label>
                      <select
                        value={formData.idResponsavelDigitacao}
                        onChange={handleFieldChange('idResponsavelDigitacao')}
                        className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                      >
                        <option value="">Selecione</option>
                        {digitadores.map((item) => (
                          <option key={item.idResponsavelDigitacao} value={item.idResponsavelDigitacao}>
                            {item.nomeResponsavelDigitacao}
                          </option>
                        ))}
                      </select>
                      {formErrors.idResponsavelDigitacao ? (
                        <p className="text-xs text-rose-600">{formErrors.idResponsavelDigitacao}</p>
                      ) : null}
                    </div>

                    <div className="space-y-2 sm:col-span-2">
                      <label className="text-sm font-medium text-slate-700">Anexos (imagens e PDF) <span className="text-rose-500">*</span></label>
                      <input
                        type="file"
                        multiple
                        accept="image/*,application/pdf"
                        onChange={handleFileChange}
                        className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                      />
                      {formErrors.anexos ? <p className="text-xs text-rose-600">{formErrors.anexos}</p> : null}

                      {anexosSalvos.length > 0 ? (
                        <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                          <p className="font-medium text-slate-700">Já anexados</p>
                          {anexosSalvos.map((anexo) => (
                            <div key={anexo.id} className="flex items-center justify-between gap-2">
                              <span className="truncate">{anexo.nome}</span>
                              <button
                                type="button"
                                onClick={() => abrirConfirmacaoRemoverAnexo(anexo)}
                                className="inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-red-200 bg-white px-2.5 py-1 text-xs font-medium text-red-600 transition hover:border-red-300 hover:bg-red-50"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                                Remover
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : null}

                      {anexosSelecionados.length > 0 ? (
                        <div className="space-y-1 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                          {editingId ? <p className="font-medium text-slate-700">Novos (enviados ao salvar)</p> : null}
                          {anexosSelecionados.map((file) => (
                            <div key={`${file.name}-${file.size}`} className="flex items-center justify-between gap-2">
                              <span className="truncate">{file.name}</span>
                              <div className="flex flex-shrink-0 items-center gap-2">
                                <span>{formatFileSize(file.size)}</span>
                                <button
                                  type="button"
                                  onClick={() => handleRemoverAnexoSelecionado(file)}
                                  className="inline-flex h-5 w-5 items-center justify-center rounded text-slate-400 transition hover:bg-slate-200 hover:text-rose-600"
                                  aria-label={`Remover ${file.name}`}
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="w-full rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600 sm:w-auto"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                    >
                      {submitting ? 'Salvando...' : editingId ? 'Salvar alterações' : 'Salvar concretagem'}
                    </button>
                  </div>
                </>
              ) : null}
            </form>
          </div>
        ) : null
      }
    </div >
  );
}

export default ConcretagensPage;


